/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import debounce from 'lodash/debounce';
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import { EApprovalStepStatus, IApprovalFlowExecutionStep } from "../../../../interfaces/approvalFlowExecutionStep";
import { EEntity } from "../../../../components/table/filter/useTables";
import { listApprovalFlowExecutionSteps, showApprovalFlowExecutionStep, createApprovalFlowExecutionStep, updateApprovalFlowExecutionStep } from "@/services";
import { updateValue } from "@/utils/updateValue";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";

const DEFAULT: IApprovalFlowExecutionStep = {
  id: null,
  name: null,
  idApprovalFlow: null,
  startedAt: null,
  status: null,
  createdIn: null,
  idStep: null,
  idApprovalFlowExecution: null,
  isFinal: null
};


const approvalStepStatusOptions = [
  { id: EApprovalStepStatus.Pending, name: 'Pendente' },
  { id: EApprovalStepStatus.InProgress, name: 'Em andamento' },
  { id: EApprovalStepStatus.Completed, name: 'Concluído' },
  { id: EApprovalStepStatus.OnHold, name: 'Em espera' },
  { id: EApprovalStepStatus.Rejected, name: 'Rejeitado' },
  { id: EApprovalStepStatus.Canceled, name: 'Cancelado' },
  { id: EApprovalStepStatus.Failed, name: 'Falhou' },
  { id: EApprovalStepStatus.Delayed, name: 'Atrasado' },
  { id: EApprovalStepStatus.UnderReview, name: 'Em revisão' },
  { id: EApprovalStepStatus.Approved, name: 'Aprovado' },
  { id: EApprovalStepStatus.NeedsReview, name: 'Precisa de revisão' },
  { id: EApprovalStepStatus.Finalized, name: 'Finalizado' },
  { id: EApprovalStepStatus.Waiting, name: 'Aguardando' },
];

export const useApprovalFlowExecutionSteps = ({
  filter: filterParam,
  includes = [],
  disabledShowId,
  disabledGetAll,
  limit: limitParam,
  idApprovalFlowExecution
}: {
  disabledGetAll?: boolean;
  filter?: Record<string, any>;
  includes?: string[];
  disabledShowId?: boolean;
  limit?: number;
  idApprovalFlowExecution?: number;
}) => {
  const { idApprovalFlowExecution: id } = useParams();
  const [approvalFlowExecutionSteps, setApprovalFlowExecutionSteps] = useState<IApprovalFlowExecutionStep[]>([]);
  const [approvalFlowExecutionStep, setApprovalFlowExecution] = useState<IApprovalFlowExecutionStep>(DEFAULT);
  const { filter, setFilter, handleFilter, sort, handleSort, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterParam, defaultLimit: limitParam || 25 });
  const [totalItems, setTotalItems] = useState<number>(0);
  const [enableChangePositionMap, setEnableChangePositionMap] =
    useState<boolean>(false);
  const { goEdit: goEditMeasurement } = useEntityNavigation({
    basePath: "/towermanagement/measurements",
  });
  const { goEdit: goEditActivity } = useEntityNavigation({
    basePath: "/towermanagement/activities",
  });

  const [loading, setLoading] = useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleApprovalFlowExecutionStep = (
    key: string,
    value: any,
    custom?: (v: any) => any
  ) => {
    setApprovalFlowExecution((curr: any) => {
      let newData;
      if (!custom) newData = updateValue(curr, key, value);
      if (custom) newData = custom(curr);
      return { ...curr, ...newData };
    });
  };

  // Etapa não tem tela própria: abre a entidade dona do fluxo
  const goEdit = (_id: string | number, record?: IApprovalFlowExecutionStep) => {
    const execution = record?.approvalFlowExecution;
    if (execution?.entityType === EEntity.TWM_MEASUREMENTS) {
      goEditMeasurement(execution.idEntity!);
      return;
    }
    if (execution?.entityType === EEntity.TWM_ACTIVITIES) {
      goEditActivity(execution.idEntity!);
    }
  };


  const getAll = useCallback(debounce(({
    page = 1,
    limit: limitP,
    filter,
    sort,
  }: {
    page?: number;
    limit?: number;
    filter?: any;
    sort?: Record<string, any>;
  }) => {
    setLoading(true);
    listApprovalFlowExecutionSteps({
      includes: [...includes, 'userAssigned', 'approvalFlowExecution', 'approvalFlowExecutionStep', 'groupAssigned'],
      filter: filter,
      page,
      limit: limitP,
      select: ['approvalFlowExecution.id', 'approvalFlowExecution.name', 'approvalFlowExecution.idEntity', 'approvalFlowExecution.entityType'],
      sort
    })
      .then(
        ({
          approvalFlowExecutionSteps: listApprovalFlowExecutionSteps,
          limit,
          currentPage,
          totalItems,
        }) => {
          setApprovalFlowExecutionSteps(listApprovalFlowExecutionSteps);
          
          setLimit(Number(limit));
          setTotalItems(Number(totalItems));
        }
      )
      .finally(() => setLoading(false));
  }, 500), []);

  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showApprovalFlowExecutionStep({ id, includes: [...includes] })
      .then(({ approvalFlowExecutionStep: approvalFlowExecutionStepsShow }) => {
        setApprovalFlowExecution(approvalFlowExecutionStepsShow);
      })
      .finally(() => setLoading(false));
  };

  const search = useEntitySearch({
    list: listApprovalFlowExecutionSteps,
    key: "approvalFlowExecutionSteps",
    includes,
    filter,
  });


  const create = () => {
    setLoading(true);
    createApprovalFlowExecutionStep({ approvalFlowExecutionStep })
      .then(({ approvalFlowExecutionStep: approvalFlowExecutionCreate }) => {
        toast.success(`Etapa de aprovação de fluxo criada com sucesso!`);
        goEdit(approvalFlowExecutionCreate.id!);
      })
      .finally(() => setLoading(false));
  };

  const update = () => {
    setLoading(true);
    updateApprovalFlowExecutionStep({ id: approvalFlowExecutionStep.id!, approvalFlowExecutionStep })
      .then(() => {
        toast.success(`Etapa de aprovação de fluxo atualizada com sucesso!`);
        get({ id: approvalFlowExecutionStep.id })
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setFilter((f) => ({ ...f, ...(filterParam || {}) }))
  }, [filterParam])


  useEffect(() => {
    if (!disabledGetAll) getAll({ page, limit: limit, filter, sort });
  }, [page, limit, filter, sort]);

  useEffect(() => {
    const idCheck = id || idApprovalFlowExecution
    if (idCheck && (!disabledShowId || idApprovalFlowExecution)) {
      if (idCheck !== "new") get({ id: idCheck });
      return;
    }
  }, []);

  return {
    approvalFlowExecutionSteps,
    approvalFlowExecutionStep,
    loading,
    setPage,
    setLimit,
    page,
    limit,
    goEdit,
    get,
    handleApprovalFlowExecutionStep,
    create,
    id,
    update,
    handleFilter,
    handleSort,
    enableChangePositionMap,
    setEnableChangePositionMap,
    totalItems,
    search,
    setFilter,
    approvalStepStatusOptions
  };
};
